import { Link } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { errorDetail } from '../services/api';
import { PacketDownloads, usePacketDeterminations } from './CEPacketDownloads';

const CTRL_LABELS = {
  'CE-CTRL-001': 'Operational event chronology',
  'CE-CTRL-002': 'Change traceability',
  'CE-CTRL-003': 'Command activity record',
  'CE-CTRL-004': 'Incident reconstruction',
  'CE-CTRL-005': 'Evidence completeness',
};

const ALL_CONTROLS = Object.keys(CTRL_LABELS);

// Fleet-scale async generation UI flag (Gate 1d) — default OFF. While off,
// the page behaves exactly as it did before this gate: a single blocking
// POST to the synchronous compatibility-shim endpoint. Flipping this on
// switches to submit (POST /ce/generation-jobs) -> poll (GET .../{id}) ->
// render (GET .../{id}/result) — this flag is deliberately independent
// of, and gated separately from, the SERVER-side CE_ASYNC_GENERATION_
// ENABLED flag (ce_router.py) that gates the submission endpoint itself.
//
// BINDING CORRECTION (review-caught): this is a Create React App build,
// deployed as pre-built static files to Azure Static Web Apps via
// .github/workflows/azure-deploy.yml / customer-deploy.yml — NOT an App
// Service. `REACT_APP_*` values are substituted by webpack at `npm run
// build` time (CRA's standard mechanism) and baked verbatim into the
// shipped JS bundle; there is no runtime-config indirection anywhere in
// this codebase (no window.__CONFIG__, no /config fetch, no nginx
// envsubst step) that could change an ALREADY-BUILT bundle's value.
// Concretely, enabling this flag for a real environment requires:
//   1. Adding REACT_APP_CE_ASYNC_GENERATION_UI_ENABLED=true to the
//      relevant workflow's build step (both deploy workflows currently
//      hardcode only REACT_APP_API_URL; this new var would need to be
//      added there, per target branch/customer/environment).
//   2. Triggering a NEW build + deploy (a push, or a workflow_dispatch
//      run) — an already-deployed bundle can NEVER pick this up merely
//      by restarting anything, because there is nothing running at
//      request time to restart — it's static files behind a CDN.
// This is a materially different (and slower) rollout step than the
// server-side flags above, which ARE plain App Service configuration
// settings a restart picks up. Do not conflate the two when planning a
// rollout.
const CE_ASYNC_GENERATION_UI_ENABLED =
  (process.env.REACT_APP_CE_ASYNC_GENERATION_UI_ENABLED || 'false').toLowerCase() === 'true';

// Bounded backoff polling (approved plan): starts responsive right after a
// real state transition, backs off during long quiet stretches rather than
// hammering the poll endpoint for the whole duration of a real, possibly
// multi-minute Foster-Rd-scale run — matters more here than for
// AdversarySimulation.jsx's flat-2s precedent for exactly that reason.
const POLL_INITIAL_MS = 2000;
const POLL_MAX_MS = 10000;
const POLL_BACKOFF_FACTOR = 1.5;

// AWAITING_REVIEW is terminal for the POLLER even though the job will later
// resume: nothing the client does advances it, only an admin authorizing the
// partial seal. Omitting it meant the poller never stopped — the panel sat on
// the generic "Working…" label forever with no sign a human decision was
// required. Not reachable today (the async path is flag-gated, default off),
// but it would have shipped live the moment that flag flipped.
const ASYNC_TERMINAL_STATES = ['COMPLETED', 'FAILED', 'CANCELLED', 'AWAITING_REVIEW'];

function toDatetimeLocal(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function datetimeLocalToISO(val) {
  return val ? val + ':00Z' : '';
}

function dateOnly(iso) {
  return iso ? iso.slice(0, 10) : '';
}

export default function CEEvidencePacket() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [generating, setGenerating] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [genError, setGenError] = useState(null);
  const [asyncNotice, setAsyncNotice] = useState(null);
  const [result, setResult] = useState(null);
  // S16b G6e T6: ONE read of the packet's determinations, feeding both the Determinations section and
  // the determinations CSV's gate in PacketDownloads.
  const determinations = usePacketDeterminations(result?.bundle_id, Boolean(result?.bundle_id));

  // Async-path-only state (Gate 1d) — untouched, unused while
  // CE_ASYNC_GENERATION_UI_ENABLED is off.
  const [asyncJob, setAsyncJob] = useState(null);
  const pollTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(POLL_INITIAL_MS);
  const lastStageKeyRef = useRef(null);
  // The single source of truth for "which job (if any) is this component
  // currently allowed to act on." Set the instant a submission succeeds;
  // cleared on reset and on unmount. Combined with mountedRef below via
  // isStaleJob(), checked BEFORE applying any response or scheduling
  // another poll/action — without it, a slow, still-in-flight response
  // for an ABANDONED job (the user started a new submission before the
  // old one's poll returned) could land after the new job's own state
  // has already been set, silently clobbering it with stale data.
  const activeJobIdRef = useRef(null);
  // Review round 2 fix: activeJobIdRef alone protected pollOnce's own
  // poll response, but NOT the subsequent /result fetch inside
  // handleAsyncTerminal, nor the initial submit POST in
  // handleGenerateAsync, nor the cancel POST in handleCancelAsyncJob —
  // any of those resolving after unmount would still call setState on an
  // unmounted component. mountedRef closes that gap; isStaleJob() below
  // is the one guard applied after EVERY awaited network call in the
  // async path.
  const mountedRef = useRef(true);
  // Review round 3 fix: `isStaleJob()` called with no `jobId` (the
  // submit-POST call sites, before a job_id exists) previously checked
  // ONLY `mountedRef` — that does not protect against two overlapping
  // submissions from the SAME still-mounted component. `generating`
  // disabling the submit button is a state update, not synchronous DOM
  // mutation — React batches it, so a double-click (or any other rapid
  // re-entry) before that re-render commits can fire handleGenerateAsync
  // twice, dispatching two POSTs. If the OLDER of the two ("A") resolves
  // AFTER the newer one ("B") has already been applied, comparing only
  // `mountedRef` would let A's stale job_id overwrite B's — wrongly
  // reassigning activeJobIdRef.current, restarting polling for the wrong
  // job, and clobbering B's on-screen state. A monotonically increasing
  // token, captured once per handleGenerateAsync call BEFORE its POST
  // and compared after, closes this: only the response belonging to the
  // CURRENT (latest) submission attempt is ever applied.
  const submissionTokenRef = useRef(0);

  // True when a response tied to `jobId` must be dropped without
  // applying any state update: either the component has unmounted, or a
  // newer submission (or a reset) has since taken over as the active
  // job. `jobId` may be omitted for call sites that have no job identity
  // yet (the initial submit POST, before it resolves with one) — those
  // instead pass `expectedSubmissionToken` and compare against
  // submissionTokenRef, since activeJobIdRef has nothing to compare
  // against until a job_id actually exists.
  function isStaleJob(jobId, expectedSubmissionToken) {
    if (!mountedRef.current) return true;
    if (expectedSubmissionToken !== undefined) {
      return submissionTokenRef.current !== expectedSubmissionToken;
    }
    return jobId !== undefined && activeJobIdRef.current !== jobId;
  }

  useEffect(() => () => {
    mountedRef.current = false;
    clearTimeout(pollTimeoutRef.current);
    activeJobIdRef.current = null;
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get('/ce/sites');
      const list = data.sites || [];
      setSites(list);
      if (list.length === 0) {
        setLoadError('No CE sites found. Run the bridge adapter first (POST /api/ce/bridge).');
      } else {
        setSiteId(list[0].id);
      }
    } catch (err) {
      setLoadError(errorDetail(err, 'Failed to load CE sites.'));
    }
  }, []);

  useEffect(() => {
    const now = new Date();
    const minus48 = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    setPeriodEnd(toDatetimeLocal(now));
    setPeriodStart(toDatetimeLocal(minus48));
    loadSites();
  }, [loadSites]);

  const selectedSite = sites.find((s) => s.id === siteId);

  async function handleGenerate(e) {
    e.preventDefault();
    if (!siteId || !periodStart || !periodEnd) return;
    setGenError(null);
    setAsyncNotice(null);
    setResult(null);
    if (CE_ASYNC_GENERATION_UI_ENABLED) {
      await handleGenerateAsync();
    } else {
      await handleGenerateSync();
    }
  }

  async function handleGenerateSync() {
    setGenerating(true);
    try {
      const { data } = await api.post('/ce/generate-evidence-packet', {
        site_id: siteId,
        period_start: datetimeLocalToISO(periodStart),
        period_end: datetimeLocalToISO(periodEnd),
      });
      setResult(data);
    } catch (err) {
      setGenError(errorDetail(err, 'Evidence packet generation failed.'));
    } finally {
      setGenerating(false);
    }
  }

  // ── Async path (Gate 1d): submit -> poll -> render ──────────────────────

  async function handleGenerateAsync() {
    // Captured BEFORE anything else — including before the POST is even
    // constructed — so it uniquely identifies THIS call among any
    // overlapping ones (see submissionTokenRef's own comment for why
    // overlapping calls are possible despite the submit button normally
    // being disabled while generating).
    const myToken = ++submissionTokenRef.current;
    setGenerating(true);
    setAsyncJob(null);
    clearTimeout(pollTimeoutRef.current);
    activeJobIdRef.current = null;
    pollIntervalRef.current = POLL_INITIAL_MS;
    lastStageKeyRef.current = null;
    try {
      const { data } = await api.post(
        '/ce/generation-jobs',
        {
          site_id: siteId,
          period_start: datetimeLocalToISO(periodStart),
          period_end: datetimeLocalToISO(periodEnd),
        },
        { headers: { 'X-Idempotency-Key': crypto.randomUUID() } },
      );
      // Drop this response if unmounted, OR if a NEWER submission attempt
      // has since started (even if this older one resolved with a
      // perfectly valid job) — only ever act on the CURRENT attempt.
      if (isStaleJob(undefined, myToken)) return;
      activeJobIdRef.current = data.job_id;
      setAsyncJob(data);
      schedulePoll(data.job_id);
    } catch (err) {
      if (isStaleJob(undefined, myToken)) return;
      if (err?.response?.status === 409) {
        setGenError('This submission conflicted with a previous one. Please try again.');
      } else {
        setGenError(errorDetail(err, 'Evidence packet generation failed.'));
      }
      setGenerating(false);
    }
  }

  // Plain function declarations, deliberately NOT wrapped in useCallback —
  // neither is passed to another hook's dependency array or to a memoized
  // child needing referential stability, so there's nothing to gain from
  // memoizing them, and a plain mutual closure (schedulePoll's setTimeout
  // callback calls pollOnce; pollOnce calls schedulePoll again to
  // continue) is simpler to reason about than the equivalent useCallback
  // pair, which would need care about which render's closures survive.
  function schedulePoll(jobId) {
    clearTimeout(pollTimeoutRef.current);
    pollTimeoutRef.current = setTimeout(() => pollOnce(jobId), pollIntervalRef.current);
  }

  async function pollOnce(jobId) {
    let data;
    try {
      ({ data } = await api.get(`/ce/generation-jobs/${jobId}`));
    } catch (err) {
      if (isStaleJob(jobId)) return; // unmounted, or abandoned — a newer submission owns polling now

      const status = err?.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        // Not retryable — the job is gone, or we're no longer authorized
        // to see it. Retrying forever would just spin. Stop and surface
        // a clear, safe message instead of a raw error.
        setGenError(
          status === 404
            ? 'This job could not be found. It may have been removed.'
            : 'You are no longer authorized to view this job. Please sign in again.',
        );
        setGenerating(false);
        return;
      }
      // Transient (network error, timeout, 5xx, etc.) — retry with
      // GROWING bounded backoff, the same factor/cap normal polling
      // uses, rather than hammering the endpoint at whatever interval
      // was last used for a real state transition. Review round 2 fix:
      // this branch previously rescheduled at the CURRENT interval
      // unchanged, so a stretch of repeated transient failures right
      // after a real stage change (interval freshly reset to
      // POLL_INITIAL_MS) would retry every 2s indefinitely instead of
      // backing off like repeated identical-stage successes already do.
      pollIntervalRef.current = Math.min(pollIntervalRef.current * POLL_BACKOFF_FACTOR, POLL_MAX_MS);
      schedulePoll(jobId);
      return;
    }
    if (isStaleJob(jobId)) return; // unmounted, or abandoned — a newer submission owns polling now
    setAsyncJob(data);

    const stageKey = `${data.state}:${data.current_stage || ''}`;
    if (stageKey !== lastStageKeyRef.current) {
      lastStageKeyRef.current = stageKey;
      pollIntervalRef.current = POLL_INITIAL_MS;
    } else {
      pollIntervalRef.current = Math.min(pollIntervalRef.current * POLL_BACKOFF_FACTOR, POLL_MAX_MS);
    }

    if (ASYNC_TERMINAL_STATES.includes(data.state)) {
      await handleAsyncTerminal(data);
      return;
    }
    schedulePoll(jobId);
  }

  async function handleAsyncTerminal(job) {
    if (job.state === 'COMPLETED') {
      try {
        const { data } = await api.get(`/ce/generation-jobs/${job.job_id}/result`);
        // Review round 2 fix: this /result fetch is its OWN awaited call,
        // separate from the poll response that led here — a slow
        // response could still land after unmount or after the user
        // abandoned this job (cancel-then-resubmit), clobbering a NEWER
        // job's state with this stale one's result. Only pollOnce's own
        // response was guarded before; this one was not.
        if (isStaleJob(job.job_id)) return;
        setResult(data);
      } catch (err) {
        if (isStaleJob(job.job_id)) return;
        setGenError(errorDetail(err, 'Failed to load the completed evidence packet.'));
      }
    } else if (job.state === 'FAILED') {
      // Never the raw exception — error_message_safe is always a canned,
      // safe string (see ce_generation_worker.py's retry-classification
      // discipline).
      setGenError(job.error_message_safe || 'Evidence packet generation failed.');
    } else if (job.state === 'CANCELLED') {
      setAsyncNotice('Evidence packet generation was cancelled.');
    } else if (job.state === 'AWAITING_REVIEW') {
      // setAsyncNotice, not setGenError: generation SUCCEEDED and every child
      // row is committed. The bundle is incomplete and sealing it is an
      // attestation act requiring a named admin, which is the gate working, not
      // a failure. Deliberately does not fetch /result — that returns 409 for
      // this state by design.
      // Routes into the review flow rather than leaving the tech to work out where to go.
      // A notice alone is a dead end: they are told a decision is needed and not where to make it.
      setAsyncNotice({
        text: 'This packet is incomplete and needs your review before it can be finalized.',
        href: `/ce/review?site=${siteId}`,
      });
    }
    if (isStaleJob(job.job_id)) return;
    setGenerating(false);
  }

  async function handleCancelAsyncJob() {
    if (!asyncJob?.job_id) return;
    const jobId = asyncJob.job_id;
    try {
      const { data } = await api.post(`/ce/generation-jobs/${jobId}/cancel`);
      // Review round 2 fix: the cancel POST's response was entirely
      // unguarded before — a slow response could land after unmount or
      // after the user abandoned this job (e.g. it reached a terminal
      // state via polling before the cancel round-trip returned, or a
      // new submission superseded it), clobbering newer state.
      if (isStaleJob(jobId)) return;
      setAsyncJob(data);
      if (data.state === 'CANCELLED') {
        clearTimeout(pollTimeoutRef.current);
        setAsyncNotice('Evidence packet generation was cancelled.');
        setGenerating(false);
      }
      // else: still RUNNING with cancel_requested_at now set — the
      // in-flight job finishes normally; polling continues unchanged and
      // the loading panel reflects the "cancel requested" state below.
    } catch (err) {
      // Non-fatal — the next poll tick reflects true state regardless.
    }
  }

  function handleReset() {
    clearTimeout(pollTimeoutRef.current);
    activeJobIdRef.current = null;
    setResult(null);
    setGenError(null);
    setAsyncNotice(null);
    setAsyncJob(null);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="bg-gray-900 text-gray-100 p-6 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-white">Evidence Packet Generator</h1>
          <p className="text-sm text-gray-400 mt-1">
            Assemble a complete compliance evidence packet — timelines, command traces, control mappings, and a sealed bundle.
          </p>
        </div>

        {/* ── Input / Loading state ─────────────────────────────────────────── */}
        {!result && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
            {loadError ? (
              <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
                {loadError}
              </div>
            ) : generating ? (
              CE_ASYNC_GENERATION_UI_ENABLED && asyncJob ? (
                <AsyncLoadingPanel
                  siteName={selectedSite?.site_name || '…'}
                  job={asyncJob}
                  onCancel={handleCancelAsyncJob}
                />
              ) : (
                <LoadingPanel siteName={selectedSite?.site_name || '…'} />
              )
            ) : (
              <form onSubmit={handleGenerate} className="space-y-5">
                {genError && (
                  <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
                    {genError}
                  </div>
                )}
                {asyncNotice && (
                  <div className="bg-blue-900/30 border border-blue-700 rounded-lg px-4 py-3 text-sm text-blue-300 flex items-center justify-between gap-4">
                    <span>{typeof asyncNotice === 'string' ? asyncNotice : asyncNotice.text}</span>
                    {typeof asyncNotice === 'object' && asyncNotice.href && (
                      <Link
                        to={asyncNotice.href}
                        data-testid="park-review-link"
                        className="shrink-0 px-4 py-1.5 text-xs rounded-lg bg-purple-700 hover:bg-purple-600 text-white font-medium transition-colors"
                      >
                        Review this packet
                      </Link>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Site</label>
                  <select
                    value={siteId}
                    onChange={(e) => setSiteId(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  >
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.site_name} ({s.site_code})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-400 mb-1">Period</label>
                  <div className="flex gap-2 mb-3">
                    {[
                      { label: 'Last 7 days', days: 7 },
                      { label: 'Last 30 days', days: 30 },
                    ].map((preset) => (
                      <button
                        key={preset.days}
                        type="button"
                        onClick={() => {
                          const end = new Date();
                          const start = new Date(end.getTime() - preset.days * 86400000);
                          setPeriodStart(toDatetimeLocal(start));
                          setPeriodEnd(toDatetimeLocal(end));
                        }}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                    <span className="px-3 py-1.5 text-xs text-gray-500">or set a custom range below</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">From</label>
                    <input
                      type="datetime-local"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">To</label>
                    <input
                      type="datetime-local"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!siteId || generating}
                  className="px-5 py-2 text-sm rounded-lg bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-white font-medium transition-colors"
                >
                  Generate Evidence Packet
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── Results ────────────────────────────────────────────────────────── */}
        {result && !generating && (
          <>
            <PacketHeader result={result} />
            {result.idempotent_hit && <IdempotentHitNotice />}
            {result.scoped_review && <ScopedReviewWarning />}
            {!result.idempotent_hit && <ObjectsCreated result={result} />}
            {!result.idempotent_hit && <CoverageSummary result={result} />}
            {!result.idempotent_hit && <ControlsSection result={result} />}
            <BundleSection result={result} />
            <DeterminationsSection state={determinations} />
            {result.bundle_id && <CompletenessSection completeness={result.completeness} />}
            {!result.idempotent_hit && <ProvenanceSection result={result} />}
            {result.manifest && <ManifestSection manifest={result.manifest} />}
            {result.warnings?.length > 0 && <WarningsSection warnings={result.warnings} />}
            <Disclaimer text={result.disclaimer} />

            {/* Everything this run produced, in one place — and only once it is sealed.
                A generation that completes has a sealed snapshot by definition; a run that parked
                never reaches here, it routes into review instead.

                A parked job DOES have a bundle — verified on dev 2026-08-14, job 5b4d7c05 carries
                pending_bundle_id 4a3df627, which appears in the site's bundle list with
                evidence_class NULL and no seal hash, and whose PDF and CSV exports both return 200.
                It is `pending_bundle_id` exposure that is state-gated to the /review endpoint, not
                the bundle that is missing. So "no bundle exists yet" is NOT what keeps a parked
                packet out of the downloads path — the class gate is. Do not weaken the class gate
                on the belief that the id cannot be present.

                `sealed` is a RESTRICTING input only — see the permanent model at the top of
                CEPacketDownloads.jsx. It cannot authorize on its own: evidence_class is the gate,
                and passing sealed={true} for a MANUAL_ASSEMBLY or null-class result still offers
                nothing. Do not "simplify" this by dropping the class prop. */}
            <PacketDownloads
              bundleId={result.bundle_id}
              evidenceClass={result.evidence_class}
              sealed={Boolean(result.bundle_id)}
              determinations={determinations}
            />

            {/* Footer actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleReset}
                className="px-5 py-2 text-sm rounded-lg bg-purple-700 hover:bg-purple-600 text-white font-medium transition-colors"
              >
                Generate New Packet
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function LoadingPanel({ siteName }) {
  return (
    <div className="py-10 text-center space-y-4">
      <p className="text-gray-300 text-sm font-medium">
        Generating evidence packet for <span className="text-white">{siteName}</span>…
      </p>
      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
        <div className="h-full bg-purple-500 rounded-full animate-pulse w-2/3" />
      </div>
      <p className="text-xs text-gray-500">This may take 30–60 seconds</p>
    </div>
  );
}

const ASYNC_STAGE_LABELS = {
  dispatched: 'Claimed by a worker — starting…',
  generating: 'Generating evidence…',
  completed: 'Finishing up…',
  failed: 'Finishing up…',
  cancelled: 'Finishing up…',
  // Covers only the single transient render between the poll response landing
  // and the terminal handler tearing the panel down.
  awaiting_review: 'Awaiting administrator review…',
};

function AsyncLoadingPanel({ siteName, job, onCancel }) {
  const stageLabel =
    (job.current_stage && ASYNC_STAGE_LABELS[job.current_stage]) ||
    (job.state === 'QUEUED' ? 'Waiting in queue…' : 'Working…');
  const cancelPending = !!job.cancel_requested_at && job.state === 'RUNNING';

  return (
    <div className="py-10 text-center space-y-4">
      <p className="text-gray-300 text-sm font-medium">
        Generating evidence packet for <span className="text-white">{siteName}</span>…
      </p>
      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
        <div className="h-full bg-purple-500 rounded-full animate-pulse w-2/3" />
      </div>
      <p className="text-xs text-gray-500">{stageLabel}</p>
      {job.progress_message && (
        <p className="text-xs text-gray-500">{job.progress_message}</p>
      )}
      {cancelPending ? (
        <p className="text-xs text-amber-300">
          Cancellation requested — this job has no interruption point, so it will finish
          normally rather than stop early.
        </p>
      ) : (
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

function PacketHeader({ result }) {
  const isGenerated = !!result.bundle_id;
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Evidence Packet</p>
          <h2 className="text-xl font-bold text-white">{result.site_name}</h2>
          <p className="text-sm text-gray-400 mt-1">
            {dateOnly(result.period_start)} &rarr; {dateOnly(result.period_end)}
          </p>
          {result.idempotent_hit && (
            <p className="mt-2 text-xs text-blue-400">
              Returned existing bundle — identical inputs detected.
            </p>
          )}
        </div>
        <span
          className={`mt-1 px-3 py-1 text-xs font-semibold rounded-full border ${
            isGenerated
              ? 'bg-green-900/50 text-green-300 border-green-700'
              : 'bg-amber-900/50 text-amber-300 border-amber-700'
          }`}
        >
          {isGenerated ? 'GENERATED' : 'PARTIAL'}
        </span>
      </div>
    </div>
  );
}

function IdempotentHitNotice() {
  return (
    <div className="bg-blue-900/30 border border-blue-700 rounded-xl px-4 py-3 text-sm text-blue-300">
      <span className="font-semibold">Returning previously generated packet for this period.</span> This
      bundle was already sealed for the selected site and window, so it is returned as-is rather than
      rebuilt. See the Technical Manifest below for the full list of evidence objects it contains.
    </div>
  );
}

function ScopedReviewWarning() {
  return (
    <div className="bg-amber-900/30 border border-amber-700 rounded-xl px-4 py-3 text-sm text-amber-300">
      <span className="font-semibold">Scoped review packet:</span> this bundle includes representative
      evidence objects for the selected period. Full underlying observed and derived evidence remains
      preserved and available through lineage search.
    </div>
  );
}

function ObjectsCreated({ result }) {
  const obj = result.objects_created || {};
  const incTimelines = (obj.timelines || 1) - 1;
  const seedsGrouped = obj.incident_seeds_grouped;
  const incCases = obj.incident_cases || 0;
  const incNote =
    seedsGrouped != null && seedsGrouped !== incCases
      ? `(${incCases} episode groups from ${seedsGrouped} seeds)`
      : undefined;

  const rows = [
    { label: 'Timelines', value: obj.timelines, note: `(1 operational + ${incTimelines} incident)` },
    { label: 'Command Traces', value: obj.command_traces },
    { label: 'Incident Cases', value: incCases, note: incNote },
    { label: 'Control Mappings', value: obj.control_mappings },
    { label: 'Evidence Assertions', value: obj.evidence_assertions },
  ];

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">Objects Created</h3>
      <div className="divide-y divide-gray-700/60">
        {rows.map(({ label, value, note, warn }) => (
          <div key={label} className="flex items-center justify-between py-2.5">
            <span className="text-sm text-gray-300">{label}</span>
            <div className="flex items-center gap-2">
              {note && <span className="text-xs text-gray-500">{note}</span>}
              <span className={`text-sm font-semibold tabular-nums ${warn ? 'text-amber-300' : 'text-white'}`}>
                {value ?? '—'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoverageSummary({ result }) {
  const cs = result.coverage_summary;
  if (!cs) return null;

  const rows = [
    { label: 'Observed Events', key: 'observed_events' },
    { label: 'Derived Evidence Events', key: 'derived_events' },
    { label: 'Command Traces', key: 'command_traces' },
    { label: 'Incident Candidates', key: 'incident_candidates' },
  ].filter(r => cs[r.key]);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">Coverage Summary</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-700">
              <th className="pb-2 pr-4 font-medium">Category</th>
              <th className="pb-2 pr-4 font-medium text-right">Detected</th>
              <th className="pb-2 pr-4 font-medium text-right">Included</th>
              <th className="pb-2 font-medium text-right">Deferred</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/60">
            {rows.map(({ label, key }) => {
              const d = cs[key] || {};
              const deferred = d.deferred || 0;
              return (
                <tr key={key}>
                  <td className="py-2.5 pr-4 text-gray-300">{label}</td>
                  <td className="py-2.5 pr-4 text-right font-mono text-gray-200">{d.detected ?? '—'}</td>
                  <td className="py-2.5 pr-4 text-right font-mono text-gray-200">{d.included ?? '—'}</td>
                  <td className={`py-2.5 text-right font-mono font-semibold ${deferred > 0 ? 'text-amber-300' : 'text-gray-400'}`}>
                    {deferred}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProvenanceSection({ result }) {
  const prov = result.provenance_coverage;
  if (!prov) return null;

  const total = prov.total_observed_events || 0;
  const pcap = prov.pcap_retrieval || {};

  const rows = [
    { label: 'Signature Verified', key: 'signature_verified' },
    { label: 'Agent Version Present', key: 'agent_version_present' },
    { label: 'Packet SHA-256 Present', key: 'packet_sha256_present' },
    { label: 'Legacy / Unverified', key: 'legacy_unverified', warn: true },
  ];

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
      <h3 className="text-sm font-semibold text-white">Provenance Coverage</h3>
      <p className="text-xs text-gray-500">
        Total observed events in window: <span className="text-gray-300 font-semibold">{total}</span>
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-700">
              <th className="pb-2 pr-4 font-medium">Field</th>
              <th className="pb-2 pr-4 font-medium text-right">Count</th>
              <th className="pb-2 font-medium text-right">%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/60">
            {rows.map(({ label, key, warn }) => {
              const d = prov[key] || {};
              return (
                <tr key={key}>
                  <td className={`py-2.5 pr-4 ${warn ? 'text-amber-300' : 'text-gray-300'}`}>{label}</td>
                  <td className="py-2.5 pr-4 text-right font-mono text-gray-200">{d.count ?? '—'}</td>
                  <td className="py-2.5 text-right font-mono text-gray-400">{d.percent != null ? `${d.percent}%` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500 italic">
        Events ingested before CE v1.1 (2026-07-14) do not carry HMAC signatures or packet hashes.
        They are preserved as-is and labeled Legacy / Unverified.
      </p>

      {/* pcap retrieval sub-section */}
      <div className="border-t border-gray-700 pt-4">
        <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Packet Retrieval Status</h4>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {['available', 'aged_out', 'not_indexed', 'unknown'].map((k) => (
            <div key={k} className="bg-gray-700/50 rounded p-2 text-center">
              <p className="text-xs text-gray-500 capitalize">{k.replace('_', ' ')}</p>
              <p className={`text-lg font-bold tabular-nums mt-0.5 ${k === 'unknown' && (pcap[k] || 0) > 0 ? 'text-amber-300' : k === 'available' && (pcap[k] || 0) > 0 ? 'text-green-300' : 'text-gray-300'}`}>
                {pcap[k] ?? 0}
              </p>
            </div>
          ))}
        </div>
        {pcap.note && (
          <p className="text-xs text-gray-500 italic">{pcap.note}</p>
        )}
      </div>
    </div>
  );
}

function ControlsSection({ result }) {
  const mapped = new Set(result.controls_mapped || []);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">Regulatory Controls</h3>
      <div className="divide-y divide-gray-700/60">
        {ALL_CONTROLS.map((ctrl) => {
          const ok = mapped.has(ctrl);
          return (
            <div key={ctrl} className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-3">
                <span className={`text-sm font-mono font-medium ${ok ? 'text-gray-200' : 'text-gray-500'}`}>
                  {ctrl}
                </span>
                <span className="text-sm text-gray-400">{CTRL_LABELS[ctrl]}</span>
              </div>
              <span className={`text-base font-bold ${ok ? 'text-green-400' : 'text-red-500'}`}>
                {ok ? '✓' : '✗'}
              </span>
            </div>
          );
        })}
      </div>
      {result.evidence_gaps > 0 && (
        <p className="mt-3 text-xs text-amber-300">
          Evidence gaps detected: {result.evidence_gaps}
        </p>
      )}
    </div>
  );
}

function BundleSection({ result }) {
  const hasBundle = !!result.bundle_id;
  const policyWarning = (result.warnings || []).find(
    (w) => w.includes('ce_bundle_generate') || w.includes('ce_assertion_create')
  );

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">Sealed Packet</h3>
      {hasBundle ? (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-xs text-gray-500 w-28 pt-0.5 shrink-0">Packet ID</span>
            <span className="text-xs font-mono text-gray-300 break-all">{result.bundle_id}</span>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-xs text-gray-500 w-28 pt-0.5 shrink-0">Integrity Hash</span>
            <span className="text-sm font-mono text-green-300 break-all">
              sha256:{result.integrity_hash}
            </span>
          </div>
        </div>
      ) : (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg px-4 py-3 text-sm text-amber-300">
          {policyWarning || 'Packet not sealed — ce_bundle_generate policy not enabled.'}
        </div>
      )}
    </div>
  );
}

// S16b G6e T6 — the packet's Determinations section.
//
// THE RULE (plan T4/T5/T6 amendment (ii), T5 amendment (i)): every line of text below the heading is a
// `section_lines[].text` the JSON route served — built ONCE by ce_packet_determinations.section_lines,
// the same lines the PDF prints, in the same order. This component adds the heading and the styling
// and NOTHING else; it never composes, reorders, filters or rewords a line. Do not add a sentence here:
// a sentence the server did not serve is a sentence the sealed PDF does not carry.
//
// The style per served `kind` mirrors the PDF's _DETERMINATION_LINE_STYLES (ce_pdf_renderer.py): the
// three headline kinds (the PDF's "Placeholder" style) are the visibly distinct ones.
const DETERMINATION_HEADLINE_STYLE =
  'text-sm font-semibold text-red-300 bg-red-900/30 border border-red-700 rounded px-3 py-2';
const DETERMINATION_BODY_STYLE = 'text-sm text-gray-200';
const DETERMINATION_SMALL_STYLE = 'text-xs font-mono text-gray-400 break-all';
const DETERMINATION_LINE_STYLES = {
  not_readable: DETERMINATION_HEADLINE_STYLE,
  selection_error: DETERMINATION_HEADLINE_STYLE,
  refused: DETERMINATION_HEADLINE_STYLE,
  pending: DETERMINATION_BODY_STYLE,
  empty_window: DETERMINATION_BODY_STYLE,
  requirement: 'text-sm font-semibold text-white pt-3',
  head_test: 'text-sm font-mono text-gray-100',
  head_result: DETERMINATION_BODY_STYLE,
  head_population: DETERMINATION_BODY_STYLE,
  head_matches: DETERMINATION_BODY_STYLE,
  head_cites: DETERMINATION_SMALL_STYLE,
  cap: DETERMINATION_SMALL_STYLE,
  head_statement: DETERMINATION_BODY_STYLE,
  head_determination_id: DETERMINATION_SMALL_STYLE,
  head_sequence: DETERMINATION_SMALL_STYLE,
  superseded: `${DETERMINATION_SMALL_STYLE} pl-4 border-l border-gray-600`,
  // Session HD/HH, D6 (plan docs/plans/2026-10-03-session-hd-d6-packet-gate.md §3.6): the declared-break lines and
  // their cap line, as the PDF styles them ("Body" and "Small").
  declared_break: DETERMINATION_BODY_STYLE,
  declared_break_cap: DETERMINATION_SMALL_STYLE,
};
// A kind this build does not know is still PRINTED (a line is never dropped), in a style of its own
// so it does not pass for a known one.
const DETERMINATION_UNKNOWN_KIND_STYLE = 'text-sm text-amber-300';

export function DeterminationsSection({ state }) {
  if (!state || state.status === 'idle') return null;
  if (state.status === 'ok' && state.data.present === false) return null; // sealed before determinations

  let body;
  if (state.status === 'loading') {
    body = <p className="text-xs text-gray-500">Reading determinations…</p>;
  } else if (state.status === 'error') {
    // A failed read is stated, never rendered as the silence `present: false` produces.
    body = (
      <div
        data-testid="packet-determinations-error"
        className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300"
      >
        {state.error}
      </div>
    );
  } else {
    body = (
      <div data-testid="packet-determinations-lines" className="space-y-1">
        {state.data.section_lines.map((line, i) => {
          const Tag = line.kind === 'requirement' ? 'h4' : 'p';
          return (
            <Tag
              key={i}
              data-kind={line.kind}
              className={DETERMINATION_LINE_STYLES[line.kind] || DETERMINATION_UNKNOWN_KIND_STYLE}
            >
              {line.text}
            </Tag>
          );
        })}
      </div>
    );
  }

  return (
    <div data-testid="packet-determinations" className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">Determinations</h3>
      {body}
    </div>
  );
}

// Session EP (EA-1; plan docs/plans/2026-09-25-session-ep-der-check-pending.md §4.6) — the packet's Completeness
// section, rendered for a completed bundle whose sealed `completeness.complete === false`.
//
// THE RULE, as for the Determinations section above: every line's text is a SEALED string, verbatim — an input's
// own `reason` — or, where the sealed input carries none, a fallback that says so rather than an empty line. This
// component adds no zone, no formatting and no sentence of its own, so the page's text equals the sealed field.
// The inputs are read in a fixed order; a category line states that category's own sealed counts. A CLOSED list: an
// input absent from it prints nowhere on this page (session HD/HH, D6 added `declared_breaks`, the fifth).
const COMPLETENESS_INPUT_KEYS = [
  'derivation_coverage', 'derivation_pending', 'gap_population', 'determinations', 'declared_breaks',
];

export function CompletenessSection({ completeness }) {
  if (!completeness || completeness.complete !== false) return null;

  const lines = [];
  for (const key of COMPLETENESS_INPUT_KEYS) {
    const input = completeness[key];
    if (!input || input.complete !== false) continue;
    const reason = typeof input.reason === 'string' && input.reason.length > 0
      ? input.reason
      : `${key}: incomplete (no reason recorded)`;
    lines.push({ kind: key, text: reason });
  }
  for (const [name, cat] of Object.entries(completeness.categories || {})) {
    if (!cat || cat.complete !== false) continue;
    lines.push({ kind: 'category', text: `${name}: ${cat.included_count} of ${cat.detected_count} included` });
  }

  return (
    <div data-testid="packet-completeness" className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">Completeness</h3>
      <div data-testid="packet-completeness-lines" className="space-y-1">
        {lines.map((line, i) => (
          <p key={i} data-kind={line.kind} className="text-sm text-gray-200">
            {line.text}
          </p>
        ))}
      </div>
    </div>
  );
}

function ManifestSection({ manifest }) {
  const objects = manifest.included_objects || [];
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
      <h3 className="text-sm font-semibold text-white mb-3">
        Technical Manifest
        <span className="ml-2 text-xs text-gray-500 font-normal">({objects.length} objects)</span>
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-700">
              <th className="pb-2 pr-4 font-medium">Type</th>
              <th className="pb-2 pr-4 font-medium">ID</th>
              <th className="pb-2 font-medium">Label</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/60">
            {objects.map((obj, i) => (
              <tr key={i}>
                <td className="py-2 pr-4">
                  <span className="text-xs font-mono text-gray-400">{obj.object_type}</span>
                </td>
                <td className="py-2 pr-4">
                  <span className="text-xs font-mono text-gray-500">
                    {String(obj.object_id).slice(0, 8)}…
                  </span>
                </td>
                <td className="py-2 text-xs text-gray-300">{obj.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WarningsSection({ warnings }) {
  return (
    <div className="bg-amber-900/20 border border-amber-700/60 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-amber-300 mb-2">Warnings</h3>
      <ul className="space-y-1">
        {warnings.map((w, i) => (
          <li key={i} className="text-xs text-amber-200">
            • {w}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Disclaimer({ text }) {
  if (!text) return null;
  return (
    <p className="text-xs text-gray-500 mt-4 px-1">{text}</p>
  );
}
